import type { Theme } from "./appSlice";

import {
  DEFAULT_ROUTE_DOT_GAP,
  INACTIVE_ROUTE_COLOR,
  ROUTE_DOT_SIZE_PX,
} from "../directions/constants";

export enum ColorThemes {
  system = "Follow System",
  light = "Light",
  dark = "Dark",
}

export enum MeasurementSystems {
  metric = "METRIC",
  imperial = "IMPERIAL",
}

export type ReducedMotionTypes = "always" | "user";

export enum AccessibleColorThemes {
  colorInversionCompatible = "Color Inversion Compatible",
  highContrastLight = "High Contrast Light",
  highContrastDark = "High Contrast Dark",
}

export type AllColorThemes = AccessibleColorThemes | ColorThemes;
export const ALL_COLOR_THEME_OPTIONS = [
  ...Object.values(ColorThemes),
  ...Object.values(AccessibleColorThemes),
];

export const LIGHT_COLOR = "#fff";
export const SITUM_COLOR = "#283380";
const PRIMARY_COLOR = "#0A1435";
const SECONDARY_COLOR = "#E1E1E1";
export const BRAND_COLOR = "#283380";
const WARNING_COLOR = "#F7E9BB";
const PRIMARY_FONT_COLOR = "#0A1435";
const SECONDARY_FONT_COLOR: string = null;
const ICON_COLOR = "#555555";
const BACKGROUND_COLOR = "#ffffff";
export const HOVER_COLOR = "#ebebeb";
const PRIMARY_COLOR_DARK = "#505050";
const SECONDARY_COLOR_DARK = "#505050";
export const BRAND_COLOR_DARK = "#96a0e9";
const PRIMARY_FONT_COLOR_DARK = "#ffffff";
const SECONDARY_FONT_COLOR_DARK = "#ffffff";
const ICON_COLOR_DARK = "#ffffff";
const BACKGROUND_COLOR_DARK = "#292929";
export const HOVER_COLOR_DARK = "#353535";
const POSITIONING_DOT_COLOR = "#27357D";
const POSITIONING_OUTDOOR_DOT_COLOR = "#008000";
const POSITIONING_ICON_COLOR = "#1d2761";
const POSITIONING_ACCURACY_COLOR = "#27357D";
const INDICATION_ICON_BORDER_COLOR = "#ffffff";
const INDICATION_ICON_BACKGROUND_COLOR = "#ce2333";
const INDICATION_ICON_COLOR = "#ffffff";
const INDICATION_ICON_SIZE = 24;
const HIGH_CONSTRAST_INDICATION_ICON_SIZE = INDICATION_ICON_SIZE + 5;
export const DEFAULT_LABELS_THEME = {
  color: "#323232",
  fontSize: 16,
  offset: 0,
  outline: {
    color: "#ffffff",
    width: 0.15,
  },
};
export const DEFAULT_THEME: Theme = {
  accentLabels: DEFAULT_LABELS_THEME,
  backgroundColor: BACKGROUND_COLOR,
  brandColor: BRAND_COLOR,
  iconColor: ICON_COLOR,
  labels: DEFAULT_LABELS_THEME,
  pois: {
    borderColor: PRIMARY_COLOR,
  },
  position: {
    accuracyColor: POSITIONING_ACCURACY_COLOR,
    dotColor: POSITIONING_DOT_COLOR,
    iconColor: POSITIONING_ICON_COLOR,
    outdoorDotColor: POSITIONING_OUTDOOR_DOT_COLOR,
  },
  primaryColor: PRIMARY_COLOR,
  primaryFontColor: PRIMARY_FONT_COLOR,
  route: {
    arrowColor: PRIMARY_COLOR,
    indications: {
      backgroundColor: INDICATION_ICON_BACKGROUND_COLOR,
      borderColor: INDICATION_ICON_BORDER_COLOR,
      iconColor: INDICATION_ICON_COLOR,
      size: INDICATION_ICON_SIZE,
    },
    pathAnimationColor: PRIMARY_COLOR,
    pathColor: PRIMARY_COLOR,
    pathDotGap: DEFAULT_ROUTE_DOT_GAP,
    pathDotSize: ROUTE_DOT_SIZE_PX,
    pathUnselectedColor: INACTIVE_ROUTE_COLOR,
    showArrow: true,
  },
  secondaryColor: SECONDARY_COLOR,
  secondaryFontColor: SECONDARY_FONT_COLOR,
  warningColor: WARNING_COLOR,
};

export const DEFAULT_DARK_THEME: Theme = {
  ...DEFAULT_THEME,
  backgroundColor: BACKGROUND_COLOR_DARK,
  brandColor: BRAND_COLOR_DARK,
  iconColor: ICON_COLOR_DARK,
  primaryColor: PRIMARY_COLOR_DARK,
  primaryFontColor: PRIMARY_FONT_COLOR_DARK,
  secondaryColor: SECONDARY_COLOR_DARK,
  secondaryFontColor: SECONDARY_FONT_COLOR_DARK,
};

// Accessibility colors
const LIGHT_ACCESSIBLE_PRIMARY_COLOR = "#000199";
const LIGHT_ACCESSIBLE_SECONDARY_COLOR = "#ffffff";
const LIGHT_ACCESSIBLE_ACCENT_COLOR = "#cc0000";
const LIGHT_ACCESSIBLE_WARNING_COLOR = "#990000";
const LIGHT_ACCESSIBLE_PRIMARY_FONT_COLOR = "#000000";
const LIGHT_ACCESSIBLE_SECONDARY_FONT_COLOR = "#ffffff";
const LIGHT_ACCESSIBLE_BACKGROUND_COLOR = "#ffffff";
export const LIGHT_ACCESSIBLE_THEME: Theme = {
  accentLabels: {
    backgroundColor: LIGHT_ACCESSIBLE_ACCENT_COLOR,
    color: LIGHT_ACCESSIBLE_SECONDARY_FONT_COLOR,
    fontSize: 20,
    offset: 10,
    outline: {
      color: LIGHT_ACCESSIBLE_ACCENT_COLOR,
      width: 10,
    },
  },
  backgroundColor: LIGHT_ACCESSIBLE_BACKGROUND_COLOR,
  brandColor: LIGHT_ACCESSIBLE_PRIMARY_COLOR,
  iconColor: LIGHT_ACCESSIBLE_PRIMARY_COLOR,
  labels: {
    backgroundColor: LIGHT_ACCESSIBLE_BACKGROUND_COLOR,
    color: LIGHT_ACCESSIBLE_PRIMARY_FONT_COLOR,
    fontSize: 20,
    offset: -10,
    outline: {
      color: LIGHT_ACCESSIBLE_BACKGROUND_COLOR,
      width: 10,
    },
  },
  pois: {
    borderColor: LIGHT_ACCESSIBLE_ACCENT_COLOR,
  },
  position: {
    accuracyBorderColor: LIGHT_ACCESSIBLE_ACCENT_COLOR,
    accuracyBorderWidth: 0.5,
    accuracyColor: LIGHT_ACCESSIBLE_BACKGROUND_COLOR,
    dotColor: LIGHT_ACCESSIBLE_ACCENT_COLOR,
    iconBorderColor: LIGHT_ACCESSIBLE_BACKGROUND_COLOR,
    iconBorderWidth: 20,
    iconColor: LIGHT_ACCESSIBLE_ACCENT_COLOR,
    outdoorDotColor: LIGHT_ACCESSIBLE_WARNING_COLOR,
  },
  primaryColor: LIGHT_ACCESSIBLE_PRIMARY_COLOR,
  primaryFontColor: LIGHT_ACCESSIBLE_PRIMARY_FONT_COLOR,
  route: {
    borderColor: LIGHT_ACCESSIBLE_PRIMARY_FONT_COLOR,
    borderWidth: 1,
    indications: {
      backgroundColor: LIGHT_ACCESSIBLE_PRIMARY_COLOR,
      borderColor: LIGHT_ACCESSIBLE_BACKGROUND_COLOR,
      iconColor: LIGHT_ACCESSIBLE_BACKGROUND_COLOR,
      size: HIGH_CONSTRAST_INDICATION_ICON_SIZE,
    },
    pathColor: LIGHT_ACCESSIBLE_ACCENT_COLOR,
    pathDotGap: 1,
    pathDotSize: 9,
    showArrow: false,
  },
  secondaryColor: LIGHT_ACCESSIBLE_SECONDARY_COLOR,
  secondaryFontColor: LIGHT_ACCESSIBLE_SECONDARY_FONT_COLOR,
  warningColor: LIGHT_ACCESSIBLE_WARNING_COLOR,
};

const DARK_ACCESSIBLE_PRIMARY_COLOR = "#ffff00";
const DARK_ACCESSIBLE_SECONDARY_COLOR = "#000000";
const DARK_ACCESSIBLE_ACCENT_COLOR = "#00ffff";
const DARK_ACCESSIBLE_WARNING_COLOR = "#00ffff";
const DARK_ACCESSIBLE_PRIMARY_FONT_COLOR = "#ffffff";
const DARK_ACCESSIBLE_SECONDARY_FONT_COLOR = "#000000";
const DARK_ACCESSIBLE_BACKGROUND_COLOR = "#000000";
export const DARK_ACCESSIBLE_THEME: Theme = {
  accentLabels: {
    backgroundColor: DARK_ACCESSIBLE_ACCENT_COLOR,
    color: DARK_ACCESSIBLE_BACKGROUND_COLOR,
    fontSize: 22,
    offset: 10,
    outline: {
      color: DARK_ACCESSIBLE_ACCENT_COLOR,
      width: 10,
    },
  },
  backgroundColor: DARK_ACCESSIBLE_BACKGROUND_COLOR,
  brandColor: DARK_ACCESSIBLE_PRIMARY_COLOR,
  iconColor: DARK_ACCESSIBLE_PRIMARY_COLOR,
  labels: {
    backgroundColor: DARK_ACCESSIBLE_BACKGROUND_COLOR,
    color: DARK_ACCESSIBLE_PRIMARY_FONT_COLOR,
    fontSize: 20,
    offset: -10,
    outline: {
      color: DARK_ACCESSIBLE_BACKGROUND_COLOR,
      width: 10,
    },
  },
  pois: {
    borderColor: DARK_ACCESSIBLE_ACCENT_COLOR,
  },
  position: {
    accuracyBorderColor: DARK_ACCESSIBLE_ACCENT_COLOR,
    accuracyBorderWidth: 0.5,
    accuracyColor: DARK_ACCESSIBLE_BACKGROUND_COLOR,
    dotColor: DARK_ACCESSIBLE_ACCENT_COLOR,
    iconBorderColor: DARK_ACCESSIBLE_BACKGROUND_COLOR,
    iconBorderWidth: 20,
    iconColor: DARK_ACCESSIBLE_ACCENT_COLOR,
    outdoorDotColor: DARK_ACCESSIBLE_WARNING_COLOR,
  },
  primaryColor: DARK_ACCESSIBLE_PRIMARY_COLOR,
  primaryFontColor: DARK_ACCESSIBLE_PRIMARY_FONT_COLOR,
  route: {
    borderColor: DARK_ACCESSIBLE_BACKGROUND_COLOR,
    borderWidth: 1,
    indications: {
      backgroundColor: DARK_ACCESSIBLE_PRIMARY_COLOR,
      borderColor: DARK_ACCESSIBLE_BACKGROUND_COLOR,
      iconColor: DARK_ACCESSIBLE_BACKGROUND_COLOR,
      size: HIGH_CONSTRAST_INDICATION_ICON_SIZE,
    },
    pathColor: DARK_ACCESSIBLE_ACCENT_COLOR,
    pathDotGap: 1,
    pathDotSize: 9,
    showArrow: false,
  },
  secondaryColor: DARK_ACCESSIBLE_SECONDARY_COLOR,
  secondaryFontColor: DARK_ACCESSIBLE_SECONDARY_FONT_COLOR,
  warningColor: DARK_ACCESSIBLE_WARNING_COLOR,
};

//TODO: List all ISO639_1 languages
export const SUPPORTED_LANGUAGES = [
  "am",
  "ar",
  "az",
  "bg",
  "bn",
  "bs",
  "ca",
  "de",
  "el",
  "en",
  "es",
  "eu",
  "fa",
  "fil",
  "fr",
  "gl",
  "gu",
  "ha",
  "he",
  "hi",
  "hu",
  "id",
  "it",
  "ja",
  "kk",
  "kmr",
  "kn",
  "ko",
  "lt",
  "ml",
  "mr",
  "ms",
  "my",
  "nl",
  "no",
  "pl",
  "ps",
  "pt",
  "ru",
  "si",
  "so",
  "sw",
  "ta",
  "te",
  "th",
  "tr",
  "ur",
  "uz",
  "vi",
  "zh",
] as const;

export const RTL_LANGUAGES: Array<(typeof SUPPORTED_LANGUAGES)[number]> = [
  "ar",
  "fa",
  "he",
  "kmr",
  "ps",
  "ur",
];

export const FALLBACK_LANGUAGE = "en";

export const LOCALSTORAGE_PREFIX = "user-settings_";
